/**
 * @file Filter descriptor to concatenate other filter descriptors.
 * @author Tom Verschoore de la Houssaije
 */
import { FilterOperators } from './filter-operators';
import { Predicate } from 'breeze-client';
/**
 * Set of filter descriptors to be organized and grouped with "And" and "Or" operators.
 */
var GroupFilter = /** @class */ (function () {
    /**
     * Constructor GroupFilter.
     * The created GroupFilter auto concatenate with the existing filters of the associated DataController.
     *
     * @param component The associated data component to add this filter descriptor.
     * @param left The left filter descriptor to concatenate.
     * @param right The right filter descriptor to concatenate.
     * @param operator The operator to use to concatenate left, right and associated data component filter.
     * Only FilterOperators.And and FilterOperators.Or are valid.
     * The default value is FilterOperators.And.
     */
    function GroupFilter(component, left, right, operator) {
        if (operator === void 0) { operator = FilterOperators.And; }
        /**
         * The concatenate operator. Only FilterOperators.And and FilterOperators.Or are valid.
         *
         * @default FilterOperators.And
         */
        this._operator = FilterOperators.And;
        /**
         * Array of filter descriptors to be concatenated.
         */
        this._value = [];
        if (operator !== FilterOperators.And && operator !== FilterOperators.Or) {
            throw new Error('Invalid value for operator parameter.');
        }
        this._operator = operator;
        if (left !== null || typeof left !== 'undefined') {
            this.value.push(left);
        }
        if (right !== null || typeof right !== 'undefined') {
            this.value.push(right);
        }
        if (component === null || typeof component === 'undefined') {
            return;
        }
        if (this.value.length === 0) {
            throw new Error('Cannot concatenate empty group filter');
        }
        if (component.filter) {
            this.value.push(component.filter);
            component.filter = this;
            return;
        }
        if (this.value.length < 2) {
            throw new Error('Cannot concatenate incomplete group filter.');
        }
        component.filter = this;
    }
    Object.defineProperty(GroupFilter.prototype, "value", {
        /**
         * Gets array of filter descriptors to be concatenated.
         */
        get: function () {
            return this._value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GroupFilter.prototype, "operator", {
        /**
         * Gets the concatenate operator. Only FilterOperators.And and FilterOperators.Or are valid.
         *
         * @default FilterOperators.And
         */
        get: function () {
            return this._operator;
        },
        /**
         * Sets the concatenate operator. Only FilterOperators.And and FilterOperators.Or are valid.
         */
        set: function (value) {
            this._operator = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Create method without auto concatenate behavior.
     *
     * @param left The left filter descriptor to concatenate.
     * @param right The right filter descriptor to concatenate.
     * @param operator The operator to use to concatenate left, right filter descriptor.
     * Only FilterOperators.And and FilterOperators.Or are valid.
     * The default value is FilterOperators.And.
     *
     * @returns new GroupFilter
     */
    GroupFilter.create = function (left, right, operator) {
        if (operator === void 0) { operator = FilterOperators.And; }
        return new GroupFilter(undefined, left, right, operator);
    };
    /**
     * Gives a textual representation of the descriptor.
     *
     * @returns The description in pseudocode.
     */
    GroupFilter.prototype.toText = function () {
        if (this.value.length < 2) {
            return '(Error)';
        }
        var concatenator = ' && ';
        if (this.operator === FilterOperators.Or) {
            concatenator = ' || ';
        }
        var result = '(' + this.value[0].toText();
        /* tslint:disable:no-increment-decrement */
        for (var index = 1; index < this.value.length; index++) {
            result += concatenator + this.value[index].toText();
        }
        /* tslint:enable:no-increment-decrement */
        return result + ')';
    };
    /**
     * Called internally to build the described query.
     * @param entityType The entity type getting filtered.
     * @returns The Predicate of this description.
     */
    GroupFilter.prototype.createPredicate = function (entityType) {
        if (this._value.length === 0) {
            return null;
        }
        var predicates = [];
        this._value.forEach(function (item) {
            var predicate = item.createPredicate(entityType);
            if (predicate !== null) {
                predicates.push(predicate);
            }
        });
        switch (this._operator) {
            case FilterOperators.Or:
                if (predicates.length === 0) {
                    return predicates[0];
                }
                return Predicate.or(predicates);
            case FilterOperators.And:
                if (predicates.length === 0) {
                    return predicates[0];
                }
                return Predicate.and(predicates);
            default:
                throw new Error('Operator not supported by group filter.');
        }
    };
    return GroupFilter;
}());
export { GroupFilter };
